import { Link } from "react-router-dom"

export default function TermsOfService() {
  return (
    <div className="bg-white min-h-screen">
      {/* Content Section */}
      <div className="max-w-[998px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Header */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-2 mt-[200px]">AGREEMENT</p>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600 text-lg">
              We know it's tempting to skip these Terms of Service, but it's important to establish what you can expect from us as you use GPSTOTECH services, and what we expect from you.
            </p>
          </div>

          {/* Introduction */}
          <div className="space-y-4">
            <p className="text-gray-600">
              These Terms of Service reflect the way GPSTOTECH business works, the laws that apply to our company, and certain things we've always believed to be true. As a result, these Terms of Service help define GPSTOTECH's relationship with you as you interact with our services. For example, these terms include the following topic headings:
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                What you can expect from us, which describes how we provide and develop our services
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                What we expect from you, which establishes certain rules for using our services
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                Content in GPSTOTECH services, which describes the intellectual property rights to the content you find in our services — whether that content belongs to you, GPSTOTECH, or others
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                In case of problems or disagreements, which describes other legal rights you have, and what to expect in case someone violates these terms
              </li>
            </ul>
            <p className="text-gray-600 mt-6">
              Understanding these terms is important because, to use our services, you must accept these terms.
            </p>
            <p className="text-gray-600">
              Besides these terms, we also publish a Privacy Policy. Although it's not part of these terms, we encourage you to read it to better understand how you can update, manage, export, and delete your information.
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-12">
            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">1. Agreement to Terms</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  By accessing or using GPSTOTECH's Services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, as well as our Privacy Policy. These Terms constitute a legally binding agreement between you and GPSTOTECH. If you are using our Services on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on our website or through other communications. Your continued use of our Services after such changes constitutes your acceptance of the modified Terms.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">2. Service Description and Eligibility</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  GPSTOTECH provides career guidance and professional development services through our platform. Our Services include career assessments, personalized recommendations, learning resources, and other related features. To access certain features of our Services, you must create an account and be at least 18 years old or the age of majority in your jurisdiction.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">3. User Responsibilities</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  When using our Services, you agree to act responsibly and maintain professional conduct. You must not use our Services for any illegal purposes or in any manner that could damage, disable, overburden, or impair our platform. You are responsible for all content you submit, post, or display through our Services.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  You agree not to engage in any activity that interferes with or disrupts the Services or the servers and networks connected to the Services. You must not attempt to gain unauthorized access to any portion of the Services or any other systems or networks connected to our Services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">4. Payment Terms</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  Some aspects of our Services may require payment of fees. You agree to pay all applicable fees as described on our website. All fees are non-refundable unless otherwise specified. We reserve the right to change our fees at any time, and such changes will be posted on our website with reasonable notice.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  If you choose to purchase any paid Services, you authorize us to charge your designated payment method for the total amount of your purchase. You are responsible for any taxes and for all authorized charges made to your payment method.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">5. Intellectual Property</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  The Services and all content, features, and functionality are owned by GPSTOTECH and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  While using our Services, we grant you a limited, non-exclusive, non-transferable, and revocable license to access and use our Services for their intended purpose. This license is subject to these Terms and does not include any right to resell our Services or any portion thereof.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">6. Termination</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  We may terminate or suspend your access to our Services immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Services will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  You may terminate your account at any time by contacting us. Upon termination, you will remain liable for any outstanding fees or obligations incurred before the termination date. We are not responsible for any loss or damage that may result from the termination of your account.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">7. Contact Information</h2>
              <div className="space-y-6">
                <p className="text-gray-800 text-lg leading-relaxed">
                  If you have any questions about these Terms of Service or need to contact us for any reason, our support team is here to help. We strive to provide clear and timely responses to all inquiries regarding our Terms and your use of our Services.
                </p>
                <p className="text-gray-800 text-lg leading-relaxed">
                  You can reach our support team at any time by emailing{' '}
                  <a 
                    href="mailto:support@gpstotech.com"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    support@gpstotech.com
                  </a>
                  . We aim to respond to all inquiries promptly and will work with you to address any concerns you may have about our Terms of Service.
                </p>
              </div>
            </section>
          </div>

          {/* Action Button */}
          <div className="flex mt-12 pt-8 border-t">
            <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
              Go back to home page
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
} 